import { useState } from 'react';
import { Download, X, Share } from 'lucide-react';
import { usePwaInstall } from '../hooks/usePwaInstall';
import { useTranslation } from '../i18n';

function detectBrowser(): 'chrome' | 'edge' | 'safari' | 'firefox' | 'other' {
  const ua = navigator.userAgent;
  if (/CriOS/.test(ua)) return 'chrome';
  if (/EdgA|Edg\//.test(ua)) return 'edge';
  if (/Chrome/.test(ua)) return 'chrome';
  if (/Safari/.test(ua)) return 'safari';
  if (/Firefox/.test(ua)) return 'firefox';
  return 'other';
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

type InstallProfile = 'safari' | 'firefox' | 'other';

const INSTALL_STEP_COUNT: Record<InstallProfile, number> = {
  safari: 3,
  firefox: 2,
  other: 2,
};

export function PwaInstallButton() {
  const { t } = useTranslation();
  const { canInstall, isInstalled, install } = usePwaInstall();
  const [showModal, setShowModal] = useState(false);

  if (isInstalled) return null;

  const browser = detectBrowser();
  const ios = isIOS();
  const profile: InstallProfile = ios ? 'safari' : browser === 'firefox' ? 'firefox' : browser === 'safari' ? 'safari' : 'other';
  const stepCount = INSTALL_STEP_COUNT[profile];
  const title = t(`hub:pwa.install.${profile}.title`);
  const steps = Array.from({ length: stepCount }, (_, i) => t(`hub:pwa.install.${profile}.step${i + 1}`));

  if (canInstall) {
    return (
      <button
        type="button"
        onClick={() => void install()}
        className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-meta font-semibold text-white transition-colors hover:bg-slate-700 dark:bg-slate-600 dark:hover:bg-slate-500"
      >
        <Download size={13} strokeWidth={2.5} />
        {t('hub:pwa.installButton')}
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-meta font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700/50"
      >
        <Download size={13} strokeWidth={2.5} />
        {t('hub:pwa.installButton')}
      </button>

      {showModal ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-4 pb-4 sm:items-center sm:pb-0"
          onClick={() => setShowModal(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-md dark:bg-slate-800 dark:shadow-none dark:ring-1 dark:ring-slate-600"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pwa-install-title"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3
                id="pwa-install-title"
                className="text-lead font-semibold text-slate-800 dark:text-slate-100"
              >
                {title}
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                aria-label={t('hub:pwa.close')}
              >
                <X size={16} />
              </button>
            </div>
            <ol className="space-y-3">
              {steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-800 text-meta font-bold text-white dark:bg-slate-600">
                    {i + 1}
                  </span>
                  <span className="text-field text-slate-700 dark:text-slate-300">{step}</span>
                </li>
              ))}
            </ol>
            {ios ? (
              <div className="mt-4 flex items-center justify-center gap-2 text-meta text-slate-400">
                <Share size={14} aria-hidden />
                <span>{t('hub:pwa.safariHint')}</span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
